import { ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { IToolPanelAngularComp } from 'ag-grid-angular';
import { AISearchPanelService, ColumnMapping } from './ai-search-panel.service';
import { Subscription } from 'rxjs';
import { GridApi, IToolPanelParams } from 'ag-grid-community';

@Component({
    selector: 'app-ai-search-panel',
    templateUrl: './ai-search-panel.component.html',
    styleUrls: ['./ai-search-panel.component.scss'],
})
export class AiSearchPanelComponent implements IToolPanelAngularComp, OnDestroy {
    private gridApi!: GridApi;
    private subscription: Subscription = new Subscription();

    // AI Search state
    userQuery: string = '';
    appliedQuery: string | null = null;
    isLoading: boolean = false;
    error: string | null = null;

    // Autocomplete state
    showAutocomplete: boolean = false;
    autocompleteOptions: string[] = [];
    autocompleteFilteredOptions: string[] = [];
    cursorPosition: number = 0;
    currentMatch: string = '';
    selectedAutocompleteIndex: number = -1;

    // Column mappings
    columnMappings: ColumnMapping[] = [];

    @ViewChild('userQueryInput', { static: false }) userQueryInput!: ElementRef<HTMLTextAreaElement>;

    constructor(private aiSearchService: AISearchPanelService, private cdr: ChangeDetectorRef) {}

    agInit(params: IToolPanelParams): void {
        this.gridApi = params.api;

        // Subscribe to column mappings
        this.subscription.add(
            this.aiSearchService.columnMappings$.subscribe((mappings) => {
                this.columnMappings = mappings;
                this.cdr.detectChanges();
            })
        );

        // Subscribe to applied query changes
        this.subscription.add(
            this.aiSearchService.appliedQuery$.subscribe((query) => {
                this.appliedQuery = query;
                this.cdr.detectChanges();
            })
        );

        // Subscribe to loading state
        this.subscription.add(
            this.aiSearchService.loading$.subscribe((loading) => {
                this.isLoading = loading;
                this.cdr.detectChanges();
            })
        );

        // Subscribe to error state
        this.subscription.add(
            this.aiSearchService.error$.subscribe((error) => {
                this.error = error;
                this.cdr.detectChanges();
            })
        );

        // Initialize column mappings
        this.initializeColumnMappings();
    }

    private initializeColumnMappings(): void {
        if (this.gridApi && this.gridApi.getAllGridColumns) {
            const columns = this.gridApi.getAllGridColumns().filter((col) => col.getColDef().headerName && col.getColId() !== 'ag-Grid-AutoColumn');

            this.aiSearchService.setColumnMappings(columns);
        }
    }

    onUserQueryChange(): void {
        this.checkForAutocomplete();
    }
    onUserQueryKeyDown(event: KeyboardEvent): void {
        if (event.key === '@') {
            this.showAutocompleteOptions();
        } else if (event.key === 'Escape') {
            this.hideAutocomplete();
        } else if (event.key === 'ArrowDown') {
            if (this.showAutocomplete) {
                event.preventDefault();
                this.selectNextAutocompleteOption();
            }
        } else if (event.key === 'ArrowUp') {
            if (this.showAutocomplete) {
                event.preventDefault();
                this.selectPreviousAutocompleteOption();
            }
        } else if (event.key === 'Enter') {
            if (this.showAutocomplete) {
                event.preventDefault();
                this.selectCurrentAutocompleteOption();
            }
        } else if (event.key === 'Tab') {
            if (this.showAutocomplete) {
                event.preventDefault();
                this.selectCurrentAutocompleteOption();
            }
        }
    }

    onUserQueryFocus(): void {
        if (this.userQuery.includes('@')) {
            this.checkForAutocomplete();
        }
    }

    onUserQueryBlur(): void {
        // Delay hiding autocomplete to allow for clicks
        setTimeout(() => {
            this.hideAutocomplete();
        }, 200);
    }

    private checkForAutocomplete(): void {
        const cursorPos = this.userQueryInput?.nativeElement.selectionStart || 0;
        const textBeforeCursor = this.userQuery.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            this.currentMatch = textBeforeCursor.substring(lastAtIndex + 1);
            this.autocompleteOptions = this.aiSearchService.getAvailableColumns();
            this.autocompleteFilteredOptions = this.autocompleteOptions.filter((option) => option.toLowerCase().includes(this.currentMatch.toLowerCase()));
            this.showAutocomplete = this.autocompleteFilteredOptions.length > 0;
            this.selectedAutocompleteIndex = this.showAutocomplete ? 0 : -1; // Reset selection to first item
        } else {
            this.hideAutocomplete();
        }

        this.cdr.detectChanges();
    }

    private showAutocompleteOptions(): void {
        this.autocompleteOptions = this.aiSearchService.getAvailableColumns();
        this.autocompleteFilteredOptions = [...this.autocompleteOptions];
        this.showAutocomplete = this.autocompleteFilteredOptions.length > 0;
        this.selectedAutocompleteIndex = this.showAutocomplete ? 0 : -1;
        this.cdr.detectChanges();
    }

    private hideAutocomplete(): void {
        this.showAutocomplete = false;
        this.autocompleteFilteredOptions = [];
        this.currentMatch = '';
        this.selectedAutocompleteIndex = -1;
        this.cdr.detectChanges();
    }

    private selectNextAutocompleteOption(): void {
        if (this.autocompleteFilteredOptions.length > 0) {
            this.selectedAutocompleteIndex = Math.min(this.selectedAutocompleteIndex + 1, this.autocompleteFilteredOptions.length - 1);
            this.cdr.detectChanges();
        }
    }

    private selectPreviousAutocompleteOption(): void {
        if (this.autocompleteFilteredOptions.length > 0) {
            this.selectedAutocompleteIndex = Math.max(this.selectedAutocompleteIndex - 1, 0);
            this.cdr.detectChanges();
        }
    }

    private selectCurrentAutocompleteOption(): void {
        if (this.autocompleteFilteredOptions.length > 0 && this.selectedAutocompleteIndex >= 0) {
            const selectedOption = this.autocompleteFilteredOptions[this.selectedAutocompleteIndex];
            this.selectAutocompleteOption(selectedOption);
        }
    }

    selectAutocompleteOption(option: string): void {
        const cursorPos = this.userQueryInput?.nativeElement.selectionStart || 0;
        const textBeforeCursor = this.userQuery.substring(0, cursorPos);
        const textAfterCursor = this.userQuery.substring(cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const beforeAt = textBeforeCursor.substring(0, lastAtIndex);
            const newText = beforeAt + '@' + option + ' ' + textAfterCursor;
            this.userQuery = newText;

            // Set cursor position after the inserted text
            setTimeout(() => {
                const newCursorPos = beforeAt.length + 1 + option.length + 1;
                this.userQueryInput.nativeElement.setSelectionRange(newCursorPos, newCursorPos);
            }, 0);
        } else {
            // If no @ symbol, add one at cursor position
            const newText = textBeforeCursor + '@' + option + ' ' + textAfterCursor;
            this.userQuery = newText;

            // Set cursor position after the inserted text
            setTimeout(() => {
                const newCursorPos = textBeforeCursor.length + 1 + option.length + 1;
                this.userQueryInput.nativeElement.setSelectionRange(newCursorPos, newCursorPos);
            }, 0);
        }

        this.hideAutocomplete();
        this.cdr.detectChanges();
    }

    generateQueryWithAI(): void {
        if (!this.userQuery.trim()) {
            return;
        }

        this.aiSearchService.generateQueryWithAI(this.userQuery).subscribe({
            next: (query) => {
                // Automatically apply the generated query
                this.aiSearchService.applyQuery(query);
                this.userQuery = ''; // Clear the input
                this.cdr.detectChanges();
            },
            error: (error) => {
                console.error('Error generating query:', error);
                this.cdr.detectChanges();
            },
        });
    }

    copyQuery(): void {
        if (this.appliedQuery) {
            navigator.clipboard
                .writeText(this.appliedQuery)
                .then(() => {
                    console.log('Applied query copied to clipboard');
                })
                .catch((err) => {
                    console.error('Failed to copy query:', err);
                });
        }
    }

    clearQuery(): void {
        this.aiSearchService.clearQuery();
        this.userQuery = '';
        this.aiSearchService.clearError();
        this.cdr.detectChanges();
    }

    clearError(): void {
        this.aiSearchService.clearError();
        this.cdr.detectChanges();
    }
    private isValidODataQuery(query: string): boolean {
        // Basic OData query validation
        const trimmedQuery = query.trim().toLowerCase();

        // Check for basic OData operators
        const hasValidOperator = /(\$filter|\$select|\$orderby|\$top|\$skip|\$expand|\$count)/.test(trimmedQuery);

        // Check for basic syntax
        const hasValidSyntax = !trimmedQuery.includes('undefined') && !trimmedQuery.includes('null') && trimmedQuery.length > 0;

        return hasValidOperator || hasValidSyntax;
    }

    refresh(): void {
        this.initializeColumnMappings();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
